'use client';

import { useRef, useEffect, useCallback } from 'react';

/*
 * COMPONENT_CONTRACT
 *   PURPOSE: Muted hero video whose playback position is controlled by the
 *            horizontal mouse/touch position across the full viewport width.
 *            Left edge = frame 0, right edge = frame end. Bidirectional.
 *   PROPS:   src: string (video url), className?: string, aria-label?: string
 *   STATE:   Internal — video element ref, rAF pending flag, last pointer X
 *   DEPENDS: none (standalone Feature component)
 *   EMITS:   none
 *   ACCESSIBILITY: `aria-label` on video element. Respects prefers-reduced-
 *                  motion (holds frame 0). Video is muted / no controls, so it
 *                  does not trap keyboard Tab.
 * END_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-07-03] CLAWE-158: New component — mouse-scrub hero video for landing
 *                redesign. Replaces static hero graphic with an interactive
 *                video whose `currentTime` is mapped to pointer X across the
 *                viewport. rAF-throttled to ≤1 seek/frame. Bidirectional.
 */

export interface HeroVideoProps {
  src: string;
  className?: string;
  'aria-label'?: string;
}

export default function HeroVideo({ src, className, 'aria-label': ariaLabel }: HeroVideoProps) {
  const vidRef = useRef<HTMLVideoElement>(null);
  const pendingRef = useRef(false);
  const lastXRef = useRef(0);

  // --- Scrub handler (rAF-throttled, lerp-smoothed) ---
  const scrub = useCallback(() => {
    const vid = vidRef.current;
    if (!vid || vid.readyState < 1 || !vid.duration) return;
    const target = Math.min(Math.max(lastXRef.current / window.innerWidth, 0), 1) * vid.duration;
    vid.currentTime += (target - vid.currentTime) * 0.45;   // lerp — плавно, даже на медленных seek
    pendingRef.current = false;
  }, []);

  // --- Pointer event → schedule scrub ---
  const schedule = useCallback((clientX: number) => {
    lastXRef.current = clientX;
    if (pendingRef.current) return;
    pendingRef.current = true;
    requestAnimationFrame(scrub);
  }, [scrub]);

  // --- Event bindings (pointermove + touchmove) ---
  useEffect(() => {
    const onPointer = (e: PointerEvent) => schedule(e.clientX);
    const onTouch = (e: TouchEvent) => {
      if (e.touches[0]) schedule(e.touches[0].clientX);
    };
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) {
      window.addEventListener('pointermove', onPointer, { passive: true });
      window.addEventListener('touchmove', onTouch, { passive: true });
    }
    return () => {
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('touchmove', onTouch);
    };
  }, [schedule]);

  // --- Show first frame on load ---
  useEffect(() => {
    const vid = vidRef.current;
    if (!vid) return;
    const onReady = () => {
      if (vid.readyState >= 1 && vid.duration) {
        vid.currentTime = 0;
      }
    };
    vid.addEventListener('loadedmetadata', onReady);
    return () => vid.removeEventListener('loadedmetadata', onReady);
  }, []);

  return (
    <video
      ref={vidRef}
      src={src}
      muted
      playsInline
      preload="auto"
      aria-label={ariaLabel}
      className={className}
    />
  );
}
